var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_TENANT_TEMPLATE from 'api/mutations/ADD_TENANT_TEMPLATE';
import ASSIGN_TEMPLATE_ELEMENT from 'api/mutations/ASSIGN_TEMPLATE_ELEMENT';
import REMOVE_TENANT_TEMPLATE from 'api/mutations/REMOVE_TENANT_TEMPLATE';
import UPDATE_TENANT_TEMPLATE from 'api/mutations/UPDATE_TENANT_TEMPLATE';
import WITHDRAW_TEMPLATE_ELEMENT from 'api/mutations/WITHDRAW_TEMPLATE_ELEMENT';
import GET_CURRENT_TENANT_TEMPLATES from 'api/queries/GET_CURRENT_TENANT_TEMPLATES';
export const initialState = {
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
};
export const fetchTemplates = createAsyncThunk('templates/fetchTemplates', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, rejectWithValue }) {
    const state = getState();
    const { pageInfo } = state.parameters.templates;
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_TEMPLATES,
            variables: {
                first: 25,
                after: pageInfo.endCursor,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.templates;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addTemplate = createAsyncThunk('templates/addTemplate', (_a) => __awaiter(void 0, [_a], void 0, function* ({ tenantId, name, elementIds = [], }) {
    try {
        const templateData = yield client.mutate({
            mutation: ADD_TENANT_TEMPLATE,
            variables: { tenantId, name, elementIds },
            fetchPolicy: 'no-cache',
        });
        return templateData.data.addModelSpecificationTemplate.modelSpecificationTemplate;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const updateTemplate = createAsyncThunk('templates/updateTemplate', (_a) => __awaiter(void 0, [_a], void 0, function* ({ templateId, name }) {
    try {
        const templateData = yield client.mutate({
            mutation: UPDATE_TENANT_TEMPLATE,
            variables: { templateId, name },
        });
        return templateData.data.updateModelSpecificationTemplate.modelSpecificationTemplate;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const removeTemplate = createAsyncThunk('templates/removeTemplate', (templateId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_TENANT_TEMPLATE,
            variables: { templateId },
        });
        return templateId;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const assignTemplateElement = createAsyncThunk('templates/assignTemplateElement', (_a) => __awaiter(void 0, [_a], void 0, function* ({ templateId, elementId }) {
    const response = yield client.mutate({
        mutation: ASSIGN_TEMPLATE_ELEMENT,
        variables: { templateId, elementId },
    });
    return {
        templateId,
        assignedTemplateElement: response.data.assignModelSpecificationTemplateElement.assignedTemplateElement,
    };
}));
export const withdrawTemplateElement = createAsyncThunk('templates/withdrawTemplateElement', (_a) => __awaiter(void 0, [_a], void 0, function* ({ templateId, templateElementId }) {
    yield client.mutate({
        mutation: WITHDRAW_TEMPLATE_ELEMENT,
        variables: { templateElementId },
    });
    return { templateId, templateElementId };
}));
const templatesSlice = createSlice({
    name: 'templates',
    initialState,
    reducers: {
        resetTemplates(state) {
            state.items = [];
            state.pageInfo = initialState.pageInfo;
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTemplates.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchTemplates.fulfilled, (state, action) => {
            var _a;
            const newTemplates = action.payload.edges.map((edge) => edge.node);
            const uniqueTemplates = newTemplates.filter((newTemplate) => !state.items.some((existingTemplate) => existingTemplate.id === newTemplate.id));
            state.items = [...state.items, ...uniqueTemplates];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchTemplates.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(addTemplate.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addTemplate.fulfilled, (state, action) => {
            state.items = [
                ...state.items,
                Object.assign(Object.assign({}, action.payload), { canNameBeUpdated: true, canBeRemoved: true }),
            ];
            state.status.add = 'succeeded';
        })
            .addCase(addTemplate.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add specification';
        })
            .addCase(updateTemplate.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateTemplate.fulfilled, (state, action) => {
            const { id, name } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { name });
            }
            state.status.update = 'succeeded';
        })
            .addCase(updateTemplate.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.error.message || 'Failed to add specification';
        })
            .addCase(removeTemplate.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeTemplate.fulfilled, (state, action) => {
            state.items = state.items.filter((order) => order.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeTemplate.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        })
            .addCase(assignTemplateElement.fulfilled, (state, action) => {
            const { templateId, assignedTemplateElement } = action.payload;
            const template = state.items.find((item) => item.id === templateId);
            if (template) {
                if (!template.templateElements)
                    template.templateElements = { edges: [] };
                template.templateElements.edges.push({ node: assignedTemplateElement });
            }
        })
            .addCase(withdrawTemplateElement.fulfilled, (state, action) => {
            const { templateId, templateElementId } = action.payload;
            const template = state.items.find((item) => item.id === templateId);
            if (template === null || template === void 0 ? void 0 : template.templateElements) {
                template.templateElements.edges = template.templateElements.edges.filter((edge) => edge.node.id !== templateElementId);
            }
        });
    },
});
export const { resetTemplates } = templatesSlice.actions;
export const selectTemplates = (state) => state.parameters.templates.items;
export const selectTemplateById = (state, templateId) => state.parameters.templates.items.find((template) => template.id === templateId);
export const selectTemplatesPageInfo = (state) => state.parameters.templates.pageInfo;
export const selectTemplatesStatus = (state) => state.parameters.templates.status;
export const selectTemplatesErrors = (state) => state.parameters.templates.errors;
export const selectTemplatesRefetchFlags = (state) => state.parameters.templates.refetchFlags;
export default templatesSlice.reducer;
